import type { App, DirectiveBinding } from '@vue/runtime-core';

/**
 * Plugin Vue pour les optimisations de performance globales
 */
export default {
  install(app: App<any>) {
    // Configuration globale de Vue pour la performance
    app.config.performance = import.meta.env.DEV;

    // Optimisation du rendu avec debounce
    app.config.globalProperties.$nextTickOptimized = (callback: () => void) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(callback);
      });
    };

    // Directive custom pour le lazy loading
    app.directive('lazy', {
      mounted(el: HTMLElement & { _lazyObserver?: IntersectionObserver }, binding: DirectiveBinding) {
        const options = {
          root: null,
          rootMargin: '50px',
          threshold: 0.01,
        };

        const callback: IntersectionObserverCallback = (entries, observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              if (binding.value && typeof binding.value === 'function') {
                binding.value();
              }
              observer.unobserve(el);
            }
          });
        };

        const observer = new IntersectionObserver(callback, options);
        observer.observe(el);

        // Cleanup
        el._lazyObserver = observer;
      },
      unmounted(el: HTMLElement & { _lazyObserver?: IntersectionObserver }) {
        if (el._lazyObserver) {
          el._lazyObserver.disconnect();
        }
      },
    });

    // Directive pour le prefetching
    app.directive('prefetch', {
      mounted(el: HTMLElement, binding: DirectiveBinding) {
        if (!binding.value) return;

        const prefetch = () => {
          const link = document.createElement('link');
          link.rel = 'prefetch';
          link.href = binding.value;
          document.head.appendChild(link);
        };

        // Prefetch au survol
        el.addEventListener('mouseenter', prefetch, { once: true });
        el.addEventListener('touchstart', prefetch, { once: true, passive: true });
      },
    });

    // Mixin global pour les optimisations
    app.mixin({
      beforeUnmount() {
        // Cleanup automatique des watchers et event listeners
        if (this.$) {
          this.$?.stops?.forEach((stop: () => void) => stop());
        }
      },
    });

    // Ajouter des méthodes utilitaires globales
    app.config.globalProperties.$debounce = (
      fn: Function,
      delay: number = 300
    ) => {
      let timeoutId: number | null = null;
      return function (this: any, ...args: any[]) {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        timeoutId = window.setTimeout(() => {
          fn.apply(this, args);
        }, delay);
      };
    };

    app.config.globalProperties.$throttle = (
      fn: Function,
      delay: number = 300
    ) => {
      let lastCall = 0;
      return function (this: any, ...args: any[]) {
        const now = Date.now();
        if (now - lastCall >= delay) {
          lastCall = now;
          fn.apply(this, args);
        }
      };
    };

    // Log des performances en dev
    if (import.meta.env.DEV) {
      console.log('🚀 Plugin Performance initialisé');
    }
  },
};

// Type declarations
declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $nextTickOptimized: (callback: () => void) => void;
    $debounce: (fn: Function, delay?: number) => Function;
    $throttle: (fn: Function, delay?: number) => Function;
  }

  interface ComponentInternalInstance {
    stops?: Array<() => void>;
  }
}
